import { useState, useEffect } from 'react';
import { 
  Book, 
  Cpu, 
  Server, 
  Smartphone, 
  Code, 
  CheckCircle2,
  Copy,
  ExternalLink,
  AlertCircle,
  Zap,
  ArrowRight,
  Shield,
  RefreshCw,
  Terminal,
  Play,
  Loader2,
  XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const IntegrationGuide = () => {
  const { token } = useAdminAuth();
  const { isDarkMode } = useAdminTheme();
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Test Connection State
  const [apiKeys, setApiKeys] = useState([]);
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [testApiKey, setTestApiKey] = useState('');
  const [testPhone, setTestPhone] = useState('+231770000000');
  const [testNetwork, setTestNetwork] = useState('MTN');
  const [testMessage, setTestMessage] = useState('You have received LRD 500 from John Doe. Ref: FL-TEST-001. Balance: LRD 1,500.');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Fetch available API keys
  useEffect(() => {
    const fetchApiKeys = async () => {
      try {
        const response = await axios.get(`${API}/admin/webhook-keys`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setApiKeys(response.data.filter(key => key.is_active));
      } catch (error) {
        console.error('Error fetching API keys:', error);
      }
    };
    if (token) {
      fetchApiKeys();
    }
  }, [token]);

  const runTestConnection = async () => {
    const keyToUse = testApiKey || (selectedKeyId ? apiKeys.find(k => k.key_id === selectedKeyId)?.preview : '');
    
    if (!keyToUse) {
      toast.error('Please enter an API key or select one from your existing keys');
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const response = await axios.post(
        `${API}/sms/incoming`,
        {
          phone_number: testPhone,
          network: testNetwork,
          message: testMessage,
          received_at: new Date().toISOString()
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': keyToUse
          }
        }
      );

      setTestResult({
        success: true,
        status: response.status,
        data: response.data
      });
      toast.success('Test successful! Webhook is working.');
    } catch (error) {
      setTestResult({
        success: false,
        status: error.response?.status || 'Error',
        data: error.response?.data || { error: error.message }
      });
      toast.error(`Test failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsTesting(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CodeBlock = ({ code, language = 'json', id }) => (
    <div className="relative group">
      <pre className={`rounded-lg p-4 overflow-x-auto text-sm ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
        <code className={`language-${language} ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>{code}</code>
      </pre>
      <button
        onClick={() => copyToClipboard(code, id)}
        className={`absolute top-3 right-3 p-2 rounded-lg transition-colors opacity-0 group-hover:opacity-100 ${isDarkMode ? 'bg-slate-600 text-slate-300 hover:text-white hover:bg-slate-500' : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
        data-testid={`copy-btn-${id}`}
      >
        {copiedCode === id ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );

  const webhookEndpoint = `POST /api/sms/incoming`;
  
  const examplePayload = `{
  "phone_number": "+231770000000",
  "network": "MTN",
  "message": "You have received LRD 500 from John Ref: FL-1024",
  "received_at": "2025-12-14T10:30:00Z"
}`;

  const successResponse = `{
  "status": "matched",
  "sms_id": "sms_abc123",
  "matched_transaction_id": "FL-1024",
  "parsed": {
    "amount": 500.0,
    "reference": "FL-1024",
    "sender": "John",
    "network": "MTN"
  },
  "message": "Transaction FL-1024 confirmed automatically"
}`;

  const manualReviewResponse = `{
  "status": "manual_review",
  "sms_id": "sms_def456",
  "parsed": {
    "amount": 500.0,
    "reference": null,
    "sender": "John",
    "network": "MTN"
  },
  "message": "SMS queued for manual review"
}`;

  const curlExample = `curl -X POST "https://trustbridgeflow.com/api/sms/incoming" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: flsk_your_api_key_here" \\
  -d '{
    "phone_number": "+231770000000",
    "network": "MTN",
    "message": "You have received LRD 500 from John Ref: FL-1024",
    "received_at": "2025-12-14T10:30:00Z"
  }'`;

  const pythonExample = `import requests
from datetime import datetime

API_URL = "https://trustbridgeflow.com/api/sms/incoming"
API_KEY = "flsk_your_api_key_here"

def send_sms_to_flowledger(phone_number, network, message):
    """Send SMS data to FlowLedger webhook"""
    
    payload = {
        "phone_number": phone_number,
        "network": network,
        "message": message,
        "received_at": datetime.utcnow().isoformat() + "Z"
    }
    
    headers = {
        "Content-Type": "application/json",
        "X-API-Key": API_KEY
    }
    
    response = requests.post(API_URL, json=payload, headers=headers)
    
    if response.status_code == 200:
        result = response.json()
        if result["status"] == "matched":
            print(f"Transaction {result['matched_transaction_id']} confirmed!")
        else:
            print(f"SMS queued for manual review: {result['sms_id']}")
    else:
        print(f"Error: {response.status_code} - {response.text}")
    
    return response.json()`;

  const nodejsExample = `const axios = require('axios');

const API_URL = 'https://trustbridgeflow.com/api/sms/incoming';
const API_KEY = 'flsk_your_api_key_here';

async function sendSmsToFlowLedger(phoneNumber, network, message) {
  try {
    const response = await axios.post(API_URL, {
      phone_number: phoneNumber,
      network: network,
      message: message,
      received_at: new Date().toISOString()
    }, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
    
    const result = response.data;
    
    if (result.status === 'matched') {
      console.log(\`Transaction \${result.matched_transaction_id} confirmed!\`);
    } else {
      console.log(\`SMS queued for manual review: \${result.sms_id}\`);
    }
    
    return result;
  } catch (error) {
    console.error('Error sending SMS:', error.response?.data || error.message);
    throw error;
  }
}`;

  const usbModemScript = `#!/usr/bin/env python3
"""
USB GSM Modem Integration for FlowLedger
Requires: python-gsmmodem-new, requests

Install: pip install python-gsmmodem-new requests
"""

import serial
import requests
from datetime import datetime
from gsmmodem.modem import GsmModem

# FlowLedger Configuration
FLOWLEDGER_URL = "https://trustbridgeflow.com/api/sms/incoming"
API_KEY = "flsk_your_api_key_here"

# Modem Configuration
MODEM_PORT = "/dev/ttyUSB0"  # Linux: /dev/ttyUSB0, Windows: COM3
MODEM_BAUD = 115200
NETWORK = "MTN"  # or "Orange"

def handle_incoming_sms(sms):
    """Called when SMS is received by the modem"""
    print(f"Received SMS from {sms.number}: {sms.text}")
    
    # Send to FlowLedger
    payload = {
        "phone_number": sms.number,
        "network": NETWORK,
        "message": sms.text,
        "received_at": datetime.utcnow().isoformat() + "Z"
    }
    
    try:
        response = requests.post(
            FLOWLEDGER_URL,
            json=payload,
            headers={
                "Content-Type": "application/json",
                "X-API-Key": API_KEY
            },
            timeout=30
        )
        
        result = response.json()
        if result["status"] == "matched":
            print(f"AUTO-CONFIRMED: Transaction {result['matched_transaction_id']}")
        else:
            print(f"Queued for review: {result['sms_id']}")
            
    except Exception as e:
        print(f"Error sending to FlowLedger: {e}")

def main():
    print(f"Connecting to modem on {MODEM_PORT}...")
    modem = GsmModem(MODEM_PORT, MODEM_BAUD, smsReceivedCallbackFunc=handle_incoming_sms)
    
    modem.connect()
    print(f"Connected! Signal strength: {modem.signalStrength}")
    print("Waiting for incoming SMS... (Press Ctrl+C to stop)")
    
    try:
        modem.rxThread.join()
    except KeyboardInterrupt:
        print("Shutting down...")
    finally:
        modem.close()

if __name__ == "__main__":
    main()`;

  const simBoxScript = `#!/usr/bin/env python3
"""
SIM Box / Multi-SIM Gateway Integration for FlowLedger

This script polls multiple SIM slots and forwards SMS to FlowLedger.
Adapt the SIM box API calls to match your hardware vendor's API.
"""

import requests
import time
from datetime import datetime
import logging

logging.basicConfig(level=logging.INFO)
logger = logging.getLogger(__name__)

# FlowLedger Configuration
FLOWLEDGER_URL = "https://trustbridgeflow.com/api/sms/incoming"
API_KEY = "flsk_your_api_key_here"

# SIM Box Configuration (adapt to your hardware)
SIMBOX_IP = "192.168.1.100"
SIMBOX_PORT = 8080
SIMBOX_USER = "admin"
SIMBOX_PASS = "password"
NUM_SLOTS = 8  # Number of SIM slots

# Map SIM slots to networks
SLOT_NETWORKS = {
    1: "MTN",
    2: "MTN",
    3: "Orange",
    4: "Orange",
    5: "MTN",
    6: "Orange",
    7: "MTN",
    8: "Orange"
}

def get_simbox_messages(slot):
    """
    Fetch unread SMS from a specific SIM slot.
    Adapt this function to your SIM box vendor's API.
    """
    # Example API call - modify for your hardware
    url = f"http://{SIMBOX_IP}:{SIMBOX_PORT}/api/sms/inbox"
    
    response = requests.get(url, params={
        "slot": slot,
        "status": "unread"
    }, auth=(SIMBOX_USER, SIMBOX_PASS))
    
    return response.json().get("messages", [])

def mark_message_read(slot, msg_id):
    """Mark a message as read in the SIM box"""
    url = f"http://{SIMBOX_IP}:{SIMBOX_PORT}/api/sms/mark-read"
    requests.post(url, json={
        "slot": slot,
        "message_id": msg_id
    }, auth=(SIMBOX_USER, SIMBOX_PASS))

def forward_to_flowledger(phone_number, network, message):
    """Forward SMS to FlowLedger webhook"""
    payload = {
        "phone_number": phone_number,
        "network": network,
        "message": message,
        "received_at": datetime.utcnow().isoformat() + "Z"
    }
    
    try:
        response = requests.post(
            FLOWLEDGER_URL,
            json=payload,
            headers={
                "Content-Type": "application/json",
                "X-API-Key": API_KEY
            },
            timeout=30
        )
        return response.json()
    except Exception as e:
        logger.error(f"Error forwarding to FlowLedger: {e}")
        return None

def poll_all_slots():
    """Poll all SIM slots for new messages"""
    for slot in range(1, NUM_SLOTS + 1):
        network = SLOT_NETWORKS.get(slot, "Unknown")
        
        try:
            messages = get_simbox_messages(slot)
            
            for msg in messages:
                logger.info(f"Slot {slot} ({network}): {msg['sender']} - {msg['text'][:50]}...")
                
                result = forward_to_flowledger(
                    msg["sender"],
                    network,
                    msg["text"]
                )
                
                if result:
                    if result["status"] == "matched":
                        logger.info(f"AUTO-CONFIRMED: {result['matched_transaction_id']}")
                    else:
                        logger.info(f"Queued for review: {result['sms_id']}")
                    
                    # Mark as processed
                    mark_message_read(slot, msg["id"])
                    
        except Exception as e:
            logger.error(f"Error polling slot {slot}: {e}")

def main():
    logger.info(f"Starting SIM Box poller for {NUM_SLOTS} slots...")
    logger.info(f"Forwarding to: {FLOWLEDGER_URL}")
    
    while True:
        poll_all_slots()
        time.sleep(5)  # Poll every 5 seconds

if __name__ == "__main__":
    main()`;

  const customServerScript = `#!/usr/bin/env python3
"""
Custom SIM Hosting Server Integration for FlowLedger

This is a Flask server that receives SMS from your SIM hosting infrastructure
and forwards them to FlowLedger for automatic transaction confirmation.
"""

from flask import Flask, request, jsonify
import requests
from datetime import datetime
import logging
import os

app = Flask(__name__)
logging.basicConfig(level=logging.INFO)
logger = logging.getLogger(__name__)

# FlowLedger Configuration
FLOWLEDGER_URL = os.environ.get(
    "FLOWLEDGER_URL", 
    "https://trustbridgeflow.com/api/sms/incoming"
)
FLOWLEDGER_API_KEY = os.environ.get("FLOWLEDGER_API_KEY", "flsk_your_api_key_here")

# Your internal authentication (optional)
INTERNAL_API_KEY = os.environ.get("INTERNAL_API_KEY", "your_internal_key")

def forward_to_flowledger(phone_number, network, message, received_at=None):
    """Forward SMS to FlowLedger"""
    payload = {
        "phone_number": phone_number,
        "network": network,
        "message": message,
        "received_at": received_at or datetime.utcnow().isoformat() + "Z"
    }
    
    response = requests.post(
        FLOWLEDGER_URL,
        json=payload,
        headers={
            "Content-Type": "application/json",
            "X-API-Key": FLOWLEDGER_API_KEY
        },
        timeout=30
    )
    
    return response.json()

@app.route("/health", methods=["GET"])
def health_check():
    """Health check endpoint"""
    return jsonify({"status": "healthy", "service": "sim-hosting-bridge"})

@app.route("/sms/receive", methods=["POST"])
def receive_sms():
    """
    Endpoint for your SIM hosting hardware to send SMS.
    
    Expected payload:
    {
        "from": "+231770000000",
        "to": "+231880000000",
        "text": "SMS content",
        "sim_slot": 1,
        "network": "MTN",
        "timestamp": "2025-12-14T10:30:00Z"
    }
    """
    # Optional: Verify internal API key
    api_key = request.headers.get("X-Internal-Key")
    if INTERNAL_API_KEY and api_key != INTERNAL_API_KEY:
        return jsonify({"error": "Unauthorized"}), 401
    
    data = request.json
    
    # Extract fields (adapt to your payload structure)
    phone_number = data.get("from") or data.get("sender")
    network = data.get("network", "Unknown")
    message = data.get("text") or data.get("message")
    received_at = data.get("timestamp") or data.get("received_at")
    
    if not phone_number or not message:
        return jsonify({"error": "Missing required fields"}), 400
    
    logger.info(f"Received SMS from {phone_number}: {message[:50]}...")
    
    # Forward to FlowLedger
    try:
        result = forward_to_flowledger(phone_number, network, message, received_at)
        
        if result.get("status") == "matched":
            logger.info(f"AUTO-CONFIRMED: {result['matched_transaction_id']}")
        else:
            logger.info(f"Queued for review: {result.get('sms_id')}")
        
        return jsonify({
            "success": True,
            "flowledger_result": result
        })
        
    except Exception as e:
        logger.error(f"Error forwarding to FlowLedger: {e}")
        return jsonify({
            "success": False,
            "error": str(e)
        }), 500

@app.route("/sms/batch", methods=["POST"])
def receive_batch_sms():
    """
    Batch endpoint for sending multiple SMS at once.
    
    Expected payload:
    {
        "messages": [
            {"from": "+231...", "network": "MTN", "text": "..."},
            {"from": "+231...", "network": "Orange", "text": "..."}
        ]
    }
    """
    data = request.json
    messages = data.get("messages", [])
    
    results = []
    for msg in messages:
        try:
            result = forward_to_flowledger(
                msg.get("from"),
                msg.get("network", "Unknown"),
                msg.get("text")
            )
            results.append({"success": True, "result": result})
        except Exception as e:
            results.append({"success": False, "error": str(e)})
    
    return jsonify({"results": results})

if __name__ == "__main__":
    app.run(host="0.0.0.0", port=5000, debug=True)`;

  const systemdService = `[Unit]
Description=FlowLedger SMS Bridge
After=network.target

[Service]
Type=simple
User=flowledger
WorkingDirectory=/opt/flowledger-bridge
Environment=FLOWLEDGER_URL=https://trustbridgeflow.com/api/sms/incoming
Environment=FLOWLEDGER_API_KEY=flsk_your_api_key_here
ExecStart=/usr/bin/python3 /opt/flowledger-bridge/sms_bridge.py
Restart=always
RestartSec=5

[Install]
WantedBy=multi-user.target`;

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="integration-guide-page">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <Book className="w-7 h-7 text-teal-600" />
            Integration Guide
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            Connect GSM hardware and SIM hosting systems to FlowLedger
          </p>
        </div>
      </div>

      {/* Overview Card */}
      <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-teal-900/30 to-slate-800 border-teal-700' : 'bg-gradient-to-br from-teal-50 to-white border-teal-200'}`}>
        <div className="flex items-start gap-4">
          <div className="p-3 bg-teal-600/20 rounded-xl">
            <Zap className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>How FlowLedger SMS Confirmation Works</h2>
            <div className="grid md:grid-cols-4 gap-4 mt-4">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900'}`}>1</div>
                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Receive SMS from mobile money</span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900'}`}>2</div>
                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Send to FlowLedger webhook</span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900'}`}>3</div>
                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Auto-parse & match transaction</span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900'}`}>4</div>
                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Confirm payment instantly</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Start */}
      <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Terminal className="w-5 h-5 text-emerald-600" />
          Quick Start
        </h2>
        
        <div className="space-y-4">
          <div>
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>1. Get your API Key</h3>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              Generate an API key from the <a href="/admin/webhook-keys" className="text-teal-600 hover:underline">Webhook Keys</a> page.
            </p>
          </div>

          <div>
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>2. Webhook Endpoint</h3>
            <div className={`rounded-lg p-3 flex items-center gap-3 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <code className="text-teal-600 font-mono text-sm flex-1">{webhookEndpoint}</code>
              <span className={isDarkMode ? 'text-slate-500' : 'text-gray-400'}>|</span>
              <code className={`font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>https://trustbridgeflow.com/api/sms/incoming</code>
            </div>
          </div>

          <div>
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>3. Example Payload</h3>
            <CodeBlock code={examplePayload} language="json" id="payload" />
          </div>

          <div>
            <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>4. Test with cURL</h3>
            <CodeBlock code={curlExample} language="bash" id="curl" />
          </div>
        </div>
      </div>

      {/* Test Connection Section */}
      <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-emerald-900/30 to-slate-800 border-emerald-700' : 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200'}`}>
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-emerald-50 rounded-xl">
            <Play className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Test Your Connection</h2>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              Send a test SMS payload to verify your webhook integration is working correctly.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Test Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="api-key" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>API Key</Label>
              {apiKeys.length > 0 ? (
                <div className="space-y-2">
                  <Select value={selectedKeyId} onValueChange={(val) => {
                    setSelectedKeyId(val);
                    setTestApiKey('');
                  }}>
                    <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} data-testid="test-api-key-select">
                      <SelectValue placeholder="Select an existing API key" />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
                      {apiKeys.map((key) => (
                        <SelectItem key={key.key_id} value={key.key_id}>
                          {key.name} ({key.preview})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className={`text-center text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>or</div>
                  <Input
                    id="api-key"
                    placeholder="Enter API key manually (flsk_...)"
                    value={testApiKey}
                    onChange={(e) => {
                      setTestApiKey(e.target.value);
                      setSelectedKeyId('');
                    }}
                    className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}
                    data-testid="test-api-key-input"
                  />
                </div>
              ) : (
                <Input
                  id="api-key"
                  placeholder="Enter your API key (flsk_...)"
                  value={testApiKey}
                  onChange={(e) => setTestApiKey(e.target.value)}
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}
                  data-testid="test-api-key-input"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="test-phone" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Phone Number</Label>
                <Input
                  id="test-phone"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                  data-testid="test-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="test-network" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Network</Label>
                <Select value={testNetwork} onValueChange={setTestNetwork}>
                  <SelectTrigger className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} data-testid="test-network-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
                    <SelectItem value="MTN">MTN</SelectItem>
                    <SelectItem value="Orange">Orange</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="test-message" className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>SMS Message</Label>
              <Textarea
                id="test-message"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                className={`min-h-[80px] ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                data-testid="test-message-input"
              />
            </div>

            <Button
              onClick={runTestConnection}
              disabled={isTesting}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="run-test-btn"
            >
              {isTesting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Test
                </>
              )}
            </Button>
          </div>

          {/* Test Result */}
          <div className={`rounded-xl p-4 min-h-[300px] ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
            <h4 className={`font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <Terminal className="w-4 h-4" />
              Response
            </h4>
            
            {testResult ? (
              <div className="space-y-3">
                <div className={`flex items-center gap-2 p-2 rounded-lg ${
                  testResult.success ? 'bg-emerald-50 text-emerald-600' : 'bg-red-500/20 text-red-500'
                }`}>
                  {testResult.success ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  <span className="font-medium">
                    {testResult.success ? 'Success' : 'Failed'} - Status {testResult.status}
                  </span>
                </div>
                
                <pre className="bg-gray-100 rounded-lg p-3 overflow-x-auto text-sm">
                  <code className="text-gray-700">
                    {JSON.stringify(testResult.data, null, 2)}
                  </code>
                </pre>

                {testResult.success && testResult.data?.status === 'matched' && (
                  <div className="bg-green-500/10 border border-emerald-200 rounded-lg p-3 text-sm">
                    <p className="text-emerald-600 font-medium">Transaction Confirmed!</p>
                    <p className="text-gray-500">
                      The SMS was matched to transaction <span className="text-gray-900 font-mono">{testResult.data.matched_transaction_id}</span>
                    </p>
                  </div>
                )}

                {testResult.success && testResult.data?.status === 'manual_review' && (
                  <div className="bg-yellow-500/10 border border-yellow-200 rounded-lg p-3 text-sm">
                    <p className="text-yellow-600 font-medium">Queued for Review</p>
                    <p className="text-gray-500">
                      No matching transaction found. SMS added to review queue with ID <span className="text-gray-900 font-mono">{testResult.data.sms_id}</span>
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[250px] text-gray-400">
                <Terminal className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">Run a test to see the response</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Response Examples */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            Matched Response
          </h3>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            When the SMS matches a pending transaction:
          </p>
          <CodeBlock code={successResponse} language="json" id="success-response" />
        </div>

        <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            Manual Review Response
          </h3>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            When no matching transaction is found:
          </p>
          <CodeBlock code={manualReviewResponse} language="json" id="review-response" />
        </div>
      </div>

      {/* Integration Methods */}
      <div className="space-y-6">
        <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Integration Methods</h2>

        {/* USB GSM Modem */}
        <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <Smartphone className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>USB GSM Modem</h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  Connect a USB GSM modem (Huawei E173, Sierra Wireless, etc.) to your server and automatically forward incoming SMS to FlowLedger.
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">Single SIM</span>
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">Low Cost</span>
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">Easy Setup</span>
            </div>
            
            <div>
              <h4 className="text-gray-900 font-medium mb-2">Requirements</h4>
              <ul className="text-gray-500 text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  USB GSM modem with SIM slot
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Python 3.7+ with python-gsmmodem-new
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Active mobile money SIM card
                </li>
              </ul>
            </div>

            <div>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Installation</h4>
              <CodeBlock code="pip install python-gsmmodem-new requests" language="bash" id="usb-install" />
            </div>

            <div>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Python Script</h4>
              <CodeBlock code={usbModemScript} language="python" id="usb-script" />
            </div>
          </div>
        </div>

        {/* SIM Box / Multi-SIM Gateway */}
        <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-50 rounded-xl">
                <Cpu className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SIM Box / Multi-SIM Gateway</h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  For businesses with multiple SIM cards. Connect a SIM box (GoIP, Dinstar, Ejoin) to FlowLedger and manage SMS from multiple numbers.
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}>Multi-SIM</span>
              <span className={`px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}>High Volume</span>
              <span className={`px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}>Enterprise</span>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-yellow-600 text-sm">
                  The API calls in this example are illustrative. Adapt the code to match your specific SIM box vendor's API documentation.
                </p>
              </div>
            </div>

            <div>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Supported Hardware</h4>
              <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  GoIP 4/8/16/32 port gateways
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Dinstar DWG2000 series
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Ejoin ACOM series
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Any gateway with HTTP/REST API
                </li>
              </ul>
            </div>

            <div>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Python Polling Script</h4>
              <CodeBlock code={simBoxScript} language="python" id="simbox-script" />
            </div>
          </div>
        </div>

        {/* Custom SIM Hosting Server */}
        <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-emerald-50 rounded-xl">
                <Server className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Custom SIM Hosting Server</h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  Build a bridge server that receives SMS from your custom SIM hosting infrastructure (like SIMVault) and forwards to FlowLedger.
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}>Flexible</span>
              <span className={`px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}>Custom Integration</span>
              <span className={`px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'}`}>Scalable</span>
            </div>

            <div>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Architecture</h4>
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-center gap-4 text-sm">
                  <div className="text-center">
                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center mb-2 ${isDarkMode ? 'bg-slate-600' : 'bg-gray-100'}`}>
                      <Smartphone className={`w-8 h-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                    </div>
                    <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>SIM Hardware</span>
                  </div>
                  <ArrowRight className={`w-6 h-6 ${isDarkMode ? 'text-slate-500' : 'text-gray-300'}`} />
                  <div className="text-center">
                    <div className="w-16 h-16 bg-emerald-50 rounded-lg flex items-center justify-center mb-2">
                      <Server className="w-8 h-8 text-emerald-600" />
                    </div>
                    <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Bridge Server</span>
                  </div>
                  <ArrowRight className={`w-6 h-6 ${isDarkMode ? 'text-slate-500' : 'text-gray-300'}`} />
                  <div className="text-center">
                    <div className="w-16 h-16 bg-teal-600/20 rounded-lg flex items-center justify-center mb-2">
                      <Zap className="w-8 h-8 text-teal-600" />
                    </div>
                    <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>FlowLedger</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Flask Bridge Server</h4>
              <CodeBlock code={customServerScript} language="python" id="custom-server" />
            </div>

            <div>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Systemd Service (Linux)</h4>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                Run the bridge server as a system service:
              </p>
              <CodeBlock code={systemdService} language="ini" id="systemd" />
            </div>
          </div>
        </div>
      </div>

      {/* Code Examples in Other Languages */}
      <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Code className="w-5 h-5 text-teal-600" />
          Code Examples
        </h2>

        <div className="space-y-6">
          <div>
            <h3 className={`font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <span className="w-8 h-8 bg-yellow-50 rounded-lg flex items-center justify-center text-yellow-600 text-xs font-bold">Py</span>
              Python
            </h3>
            <CodeBlock code={pythonExample} language="python" id="python-example" />
          </div>

          <div>
            <h3 className={`font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <span className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600 text-xs font-bold">JS</span>
              Node.js
            </h3>
            <CodeBlock code={nodejsExample} language="javascript" id="nodejs-example" />
          </div>
        </div>
      </div>

      {/* Security Best Practices */}
      <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <Shield className="w-5 h-5 text-emerald-600" />
          Security Best Practices
        </h2>

        <div className="grid md:grid-cols-2 gap-4">
          <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
            <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>API Key Storage</h4>
            <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Store API keys in environment variables, never in code
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Rotate keys periodically for enhanced security
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Use separate keys for development and production
              </li>
            </ul>
          </div>

          <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
            <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Network Security</h4>
            <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Always use HTTPS for webhook calls
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Implement request timeouts (30s recommended)
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Add retry logic with exponential backoff
              </li>
            </ul>
          </div>

          <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
            <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Monitoring</h4>
            <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Log all webhook responses for debugging
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Set up alerts for failed requests
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Monitor the SMS Review Queue for unmatched messages
              </li>
            </ul>
          </div>

          <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
            <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Error Handling</h4>
            <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Handle network errors gracefully with retries
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Queue failed messages for later retry
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                Implement dead-letter queue for persistent failures
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Supported SMS Formats */}
      <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <RefreshCw className="w-5 h-5 text-teal-600" />
          Supported SMS Formats
        </h2>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
          FlowLedger automatically parses SMS messages from the following mobile money providers:
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-yellow-500/10 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-yellow-600 font-semibold mb-2">MTN Mobile Money</h4>
            <p className={`text-sm font-mono p-2 rounded ${isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-gray-50 text-gray-700'}`}>
              You have received LRD 500 from John Doe. Ref: FL-1024. Balance: LRD 1,500.
            </p>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="text-orange-600 font-semibold mb-2">Orange Money</h4>
            <p className={`text-sm font-mono p-2 rounded ${isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-gray-50 text-gray-700'}`}>
              Payment received: LRD 500.00 from John Doe. Reference: FL-1024.
            </p>
          </div>
        </div>

        <div className={`mt-4 rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
          <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Parsed Fields</h4>
          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            <li><span className="text-teal-600">amount</span> - Transaction amount in LRD</li>
            <li><span className="text-teal-600">reference</span> - Transaction reference (FL-XXXX format)</li>
            <li><span className="text-teal-600">sender</span> - Sender name from the SMS</li>
            <li><span className="text-teal-600">network</span> - MTN or Orange</li>
          </ul>
        </div>
      </div>

      {/* Need Help */}
      <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700' : 'bg-gradient-to-br from-slate-800 to-slate-900 border-gray-200'}`}>
        <div className="flex items-start gap-4">
          <div className="p-3 bg-teal-600/20 rounded-xl">
            <AlertCircle className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Need Help?</h3>
            <p className="text-slate-400 text-sm mb-4">
              If you need assistance setting up your integration or have custom requirements, our team is here to help.
            </p>
            <div className="flex gap-3">
              <a href="mailto:hello@trustbridgeflow.com">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  Contact Support
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="/admin/webhook-keys">
                <Button className="bg-teal-600 hover:bg-teal-700">
                  Generate API Key
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationGuide;
